import { inject, Injectable, signal } from '@angular/core';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { MessageService } from 'primeng/api';
import { AuthService } from '../auth/auth-service';
import { UserRole } from '../../models/enums';
import { environment } from '../../../environments/environment';
import { ToastLink } from '../../models/toast-link';
import { OPERATION_TAB } from '../../constants/ride-verification.constants';

type ReviewType = 'DAMAGE' | 'VERIFICATION' | 'OPERATION_SUBMISSION';
type ReviewEvent = 'CREATED' | 'CHANGED';

interface ReviewUpdatePayload {
  type: ReviewType;
  event: ReviewEvent;
  pendingCount: number;
}

// Roles allowed to open the stream (CS_AGENT and above, no DRIVER)
const ALLOWED_ROLES = new Set<UserRole>([
  UserRole.CS_AGENT,
  UserRole.CS_SUPERVISOR,
  UserRole.FLEET_MANAGER,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
]);

const STREAM_URL = `${environment.url}/notifications/stream`;
const WATCHDOG_MS = 35_000;  // reconnect if silent for >35s
const BACKOFF_STEPS_MS = [1_000, 2_000, 5_000, 10_000];

@Injectable({ providedIn: 'root' })
export class ReviewStreamService {
  verificationCount = signal<number | undefined>(undefined);
  damageCount = signal<number | undefined>(undefined);
  operationSubmissionCount = signal<number | undefined>(undefined);

  private authService = inject(AuthService);
  private messageService = inject(MessageService);

  private abortController: AbortController | null = null;
  private watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private backoffIndex = 0;
  private running = false;

  start(role: UserRole): void {
    if (!ALLOWED_ROLES.has(role)) return;
    if (this.running) return;
    this.running = true;
    this.backoffIndex = 0;
    this.connect();
  }

  stop(): void {
    this.running = false;
    this.abortController?.abort();
    this.abortController = null;
    this.clearWatchdog();
    this.clearReconnectTimer();
  }

  private connect(): void {
    if (!this.running) return;

    this.abortController?.abort();
    this.abortController = new AbortController();

    const token = this.authService.getAccessToken();
    if (!token) {
      this.scheduleReconnect();
      return;
    }

    this.resetWatchdog();

    fetchEventSource(STREAM_URL, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      signal: this.abortController.signal,

      onopen: async (response) => {
        if (response.ok) {
          this.backoffIndex = 0;
          return;
        }
        if (response.status === 401) {
          this.running = false;
          throw new Error('SSE_401');
        }
        if (response.status === 403) {
          this.running = false;
          throw new Error('SSE_403');
        }
        throw new Error(`SSE_${response.status}`);
      },

      onmessage: (msg) => {
        this.resetWatchdog();

        if (msg.event !== 'review-update') return;
        if (!msg.data) return;

        let payload: ReviewUpdatePayload;
        try {
          payload = JSON.parse(msg.data);
        } catch {
          return;
        }

        const count = payload.pendingCount ?? 0;

        if (payload.type === 'VERIFICATION') {
          this.verificationCount.set(count > 0 ? count : undefined);
        } else if (payload.type === 'DAMAGE') {
          this.damageCount.set(count > 0 ? count : undefined);
        } else if (payload.type === 'OPERATION_SUBMISSION') {
          this.operationSubmissionCount.set(count > 0 ? count : undefined);
        }

        if (payload.event === 'CREATED') {
          this.toast(payload.type, count);
        }
      },

      onclose: () => {
        // Server closed the stream (~10 min cycle) — reconnect quickly
        if (this.running) {
          this.scheduleReconnect(1_000);
        }
      },

      onerror: (err) => {
        if (!this.running) throw err; // abort() — suppress retry
        const msg = err?.message ?? '';
        if (msg === 'SSE_401' || msg === 'SSE_403') throw err; // no retry
        this.clearWatchdog();
        this.scheduleReconnect();
        throw err; // tell the library not to auto-retry (we handle it)
      },
    });
  }

  private scheduleReconnect(delayMs?: number): void {
    if (!this.running) return;
    this.clearReconnectTimer();
    const delay = delayMs ?? BACKOFF_STEPS_MS[Math.min(this.backoffIndex, BACKOFF_STEPS_MS.length - 1)];
    this.backoffIndex = Math.min(this.backoffIndex + 1, BACKOFF_STEPS_MS.length - 1);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private resetWatchdog(): void {
    this.clearWatchdog();
    this.watchdogTimer = setTimeout(() => {
      if (this.running) {
        this.abortController?.abort();
        this.scheduleReconnect();
      }
    }, WATCHDOG_MS);
  }

  private clearWatchdog(): void {
    if (this.watchdogTimer !== null) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private toast(type: ReviewType, count: number): void {
    const labels: Record<ReviewType, { summary: string; noun: string; link: ToastLink }> = {
      VERIFICATION: {
        summary: 'New Verification', noun: 'verification',
        link: { path: '/ride-verification', queryParams: { photoApprovalStatus: 'PENDING', page: 0, size: 20 } },
      },
      DAMAGE: {
        summary: 'New Damage Report', noun: 'damage report',
        link: { path: '/damage-reports' },
      },
      OPERATION_SUBMISSION: {
        summary: 'New Operation Photos', noun: 'operation submission',
        link: { path: '/ride-verification', queryParams: { tab: OPERATION_TAB, status: 'PENDING', page: 0, size: 20 } },
      },
    };
    const { summary, noun, link } = labels[type] ?? labels.VERIFICATION;
    this.messageService.add({
      severity: 'info',
      summary,
      detail: `${count} ${noun}${count !== 1 ? 's' : ''} pending review`,
      life: 5000,
      data: { link },
    });
  }
}
